import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { MapPin, CalendarBlank, Clock, Minus, Plus, Ticket, ShieldCheck, ArrowLeft, Tag, Gift, LockKey, ShareNetwork, HourglassMedium, CheckCircle, Confetti, Heart, BookmarkSimple, Star } from "@phosphor-icons/react";
import { PageLoader, SmartImage } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { Switch } from "../../components/ui/switch";
import ShareMenu from "../../components/ShareMenu";
import { toast } from "sonner";
import api, { money, apiErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useSocial } from "../../context/SocialContext";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";
import { RefreshBar } from "../../components/RefreshBar";

function loadRazorpay() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error("Failed to load Razorpay checkout."));
    document.body.appendChild(s);
  });
}

function fmtDate(d) {
  if (!d) return "TBA";
  try { return new Date(d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" }); }
  catch { return d; }
}
const BE = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (u) => (u && typeof u === "string" && u.startsWith("/api") ? `${BE}${u}` : u);
const isPaid = (o) => o && !o.is_free && (o.mode === "public" || o.requires_payment);
const MODE_LABEL = { invite_only: "Invite only", rsvp: "RSVP", approval: "Approval required" };

function Stars({ value = 0, size = 16, onChange }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} type="button" disabled={!onChange} onClick={() => onChange?.(i)} data-testid={onChange ? `star-${i}` : undefined}
          className={onChange ? "cursor-pointer" : "cursor-default"}>
          <Star size={size} weight={i <= Math.round(value) ? "fill" : "regular"} className={i <= Math.round(value) ? "text-amber-500" : "text-muted-foreground/40"} />
        </button>
      ))}
    </div>
  );
}

function Reviews({ eventId }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [elig, setElig] = useState(null);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/events/${eventId}/reviews`).then((r) => setData(r.data));
    if (user?.role === "customer") {
      api.get(`/events/${eventId}/review-eligibility`).then((r) => {
        setElig(r.data);
        if (r.data.my_review) { setRating(r.data.my_review.rating); setText(r.data.my_review.text || ""); }
      }).catch(() => setElig(null));
    }
  }, [eventId, user]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!rating) { toast.error("Please pick a star rating."); return; }
    setBusy(true);
    try { await api.post(`/events/${eventId}/reviews`, { rating, text }); toast.success("Review saved."); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!data) return null;
  const dist = data.distribution || {};

  return (
    <div data-testid="reviews-section">
      <h3 className="font-heading text-xl font-semibold mb-3">Ratings & reviews</h3>
      <div className="flex flex-col sm:flex-row gap-6 items-start">
        <div className="text-center shrink-0">
          <div className="font-heading text-5xl font-bold tracking-tight">{data.average || 0}</div>
          <Stars value={data.average} />
          <div className="text-xs text-muted-foreground mt-1">{data.count} review{data.count === 1 ? "" : "s"}</div>
        </div>
        <div className="flex-1 w-full space-y-1">
          {[5, 4, 3, 2, 1].map((n) => {
            const c = dist[String(n)] || 0;
            const pct = data.count ? (c / data.count) * 100 : 0;
            return (
              <div key={n} className="flex items-center gap-2 text-xs">
                <span className="w-3">{n}</span><Star size={11} weight="fill" className="text-amber-500" />
                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-amber-400" style={{ width: `${pct}%` }} /></div>
                <span className="w-6 text-right text-muted-foreground">{c}</span>
              </div>
            );
          })}
        </div>
      </div>

      {user?.role === "customer" && elig && (
        <div className="mt-5 rounded-xl border border-border bg-card p-4">
          {elig.can_review ? (
            <div data-testid="write-review">
              <div className="font-semibold text-sm mb-2">{elig.my_review ? "Update your review" : "Write a review"}</div>
              <Stars value={rating} size={22} onChange={setRating} />
              <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Share your experience…" className="mt-3" data-testid="review-text" />
              <Button onClick={submit} disabled={busy} className="rounded-full mt-3" data-testid="submit-review">{busy ? "Saving…" : elig.my_review ? "Update review" : "Post review"}</Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="review-locked">{elig.reason}</p>
          )}
        </div>
      )}

      <div className="mt-5 space-y-4">
        {(data.reviews || []).map((r) => (
          <div key={r.id} className="border-b border-border pb-4 last:border-0" data-testid={`review-${r.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-semibold text-sm">{r.user_name || "Attendee"}</div>
              <Stars value={r.rating} size={13} />
            </div>
            {r.text && <p className="text-sm text-foreground/80 mt-1">{r.text}</p>}
            {user && (
              <button onClick={() => api.post(`/reviews/${r.id}/report`).then(() => toast.success("Reported to moderators.")).catch((e) => toast.error(apiErr(e)))}
                className="text-xs text-muted-foreground hover:text-destructive mt-1" data-testid={`report-review-${r.id}`}>Report</button>
            )}
          </div>
        ))}
        {(!data.reviews || data.reviews.length === 0) && <p className="text-sm text-muted-foreground">No reviews yet. Be the first to review after attending.</p>}
      </div>
    </div>
  );
}

export default function EventDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite");
  const navigate = useNavigate();
  const { user } = useAuth();
  const social = useSocial();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);
  const [selOcc, setSelOcc] = useState(null);
  const [invite, setInvite] = useState(null);
  const [approval, setApproval] = useState(null);
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState({});
  const [coupon, setCoupon] = useState("");
  const [useCredits, setUseCredits] = useState(false);
  const [quote, setQuote] = useState(null);
  const [paying, setPaying] = useState(false);
  const [rzpCfg, setRzpCfg] = useState(null);

  useEffect(() => { api.get("/payments/config").then((r) => setRzpCfg(r.data)).catch(() => {}); }, []);

  const loadEvent = () => api.get(`/events/${id}`).then((r) => {
    setEvent(r.data);
    const occs = r.data.occurrences || [];
    setSelOcc((prev) => (prev && occs.some((o) => o.id === prev) ? prev : (occs[0]?.id || null)));
  }).catch((e) => { setEvent(null); setLoadErr(apiErr(e)); }).finally(() => setLoading(false));
  const { refreshing, lastUpdated, refresh } = useAutoRefresh(loadEvent, [id]);

  useEffect(() => {
    if (!inviteToken) return;
    api.get(`/invite/${inviteToken}`).then((r) => {
      setInvite({ ...r.data, token: inviteToken });
      if (r.data.occurrence?.id) setSelOcc(r.data.occurrence.id);
    }).catch(() => toast.error("This invite link is invalid or expired."));
  }, [inviteToken]);

  const occ = useMemo(() => (event?.occurrences || []).find((o) => o.id === selOcc) || null, [event, selOcc]);
  const items = useMemo(() => Object.entries(qty).filter(([, q]) => q > 0).map(([category_id, q]) => ({ category_id, qty: q })), [qty]);
  const itemsKey = JSON.stringify(items);
  const paid = isPaid(occ);
  const [quoteNonce, setQuoteNonce] = useState(0);

  const loadApproval = () => {
    if (!occ || occ.mode !== "approval" || !user || user.role !== "customer") { setApproval(null); return; }
    api.get(`/approval-request?event_id=${id}&occurrence_id=${occ.id}`).then((r) => setApproval(r.data.request)).catch(() => setApproval(null));
  };
  useEffect(() => { loadApproval(); }, [id, selOcc, user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setQty({}); setQuote(null); }, [selOcc]);

  useEffect(() => {
    if (!open || !paid) return;
    if (!items.length) { setQuote(null); return; }
    let active = true;
    api.post("/bookings/quote", { event_id: id, occurrence_id: selOcc, items, coupon_code: coupon || null, use_credits: useCredits })
      .then((r) => { if (active) setQuote(r.data); })
      .catch((e) => { if (active) { if (coupon) toast.error(apiErr(e)); setQuote(null); } });
    return () => { active = false; };
  }, [open, itemsKey, coupon, useCredits, paid, selOcc, id, quoteNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const step = (catId, delta, max) => setQty((s) => ({ ...s, [catId]: Math.min(max, Math.max(0, (s[catId] || 0) + delta)) }));

  const openBooking = () => {
    if (event?.customer_status === "cancelled" || occ?.availability === "cancelled") {
      toast.error("This event has been cancelled and cannot be booked.");
      return;
    }
    if (event?.customer_status === "ended" || occ?.availability === "ended") {
      toast.error("This event has ended and cannot be booked.");
      return;
    }
    if (!user) { toast.info("Please log in to book."); navigate("/login"); return; }
    if (user.role !== "customer") { toast.error("Switch to a customer account to book tickets."); return; }
    setOpen(true);
  };

  const requestApproval = async () => {
    if (event?.customer_status !== "active" || occ?.availability !== "active") return;
    if (!user) { navigate("/login"); return; }
    try {
      const { data } = await api.post("/approval-request", { event_id: id, occurrence_id: occ.id, qty: 1 });
      toast.success(data.message);
      loadApproval();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const joinWaitlist = async (catId) => {
    if (event?.customer_status !== "active" || occ?.availability !== "active") return;
    if (!user) { navigate("/login"); return; }
    try {
      const { data } = await api.post("/waitlist", { event_id: id, occurrence_id: occ.id, category_id: catId, qty: 1 });
      toast.success(data.message);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const requireCustomer = () => {
    if (!user || user.role !== "customer") { toast.info("Log in as a customer to use this."); navigate("/login"); return false; }
    return true;
  };
  const onLike = async () => { if (!requireCustomer()) return; try { await social.toggleLike(id); } catch { toast.error("Couldn't update like."); } };
  const onWishlist = async () => {
    if (!requireCustomer()) return;
    try { const w = await social.toggleWishlist(id, selOcc); toast.success(w ? "Added to wishlist — we'll notify you." : "Removed from wishlist."); }
    catch { toast.error("Couldn't update wishlist."); }
  };

  const finalize = (data) => {
    toast.success(paid ? "Payment successful! 🎟️" : "Attendance confirmed! 🎟️");
    setOpen(false);
    navigate(`/bookings/${data.id}`);
  };

  const pay = async () => {
    if (event?.customer_status !== "active" || occ?.availability !== "active") {
      toast.error("This event is no longer available for booking.");
      return;
    }
    setPaying(true);
    const body = { event_id: id, occurrence_id: selOcc, items, coupon_code: coupon || null, use_credits: useCredits, invite_token: invite?.token || null };
    try {
      // Free entry (RSVP/comp) or Razorpay not configured → confirm directly (simulated fallback).
      if (!paid || !rzpCfg?.enabled) {
        const { data } = await api.post("/bookings", body);
        finalize(data);
        return;
      }
      const { data: order } = await api.post("/payments/order", body);
      if (order.free || !order.enabled) {
        const { data } = await api.post("/bookings", body);
        finalize(data);
        return;
      }
      await loadRazorpay();
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency, order_id: order.order_id,
        name: "EventVexa", description: event.title,
        prefill: { name: user?.name || "", email: user?.email || "", contact: user?.mobile || "" },
        notes: { event_id: id },
        theme: { color: "#E63946" },
        handler: async (r) => {
          try {
            const { data } = await api.post("/bookings", { ...body,
              razorpay_order_id: r.razorpay_order_id, razorpay_payment_id: r.razorpay_payment_id, razorpay_signature: r.razorpay_signature });
            finalize(data);
          } catch (e) { toast.error(apiErr(e)); setPaying(false); }
        },
        modal: { ondismiss: () => { setPaying(false); toast.info("Payment cancelled."); } },
      });
      rzp.on("payment.failed", (resp) => { toast.error(resp?.error?.description || "Payment failed. Please try again."); setPaying(false); });
      rzp.open();
    } catch (e) { toast.error(apiErr(e)); setPaying(false); }
  };

  if (loading) return <PageLoader label="Loading event" />;
  if (!event) return <div className="mx-auto max-w-3xl px-4 py-20 text-center text-muted-foreground" data-testid="event-load-error">Event not found.{loadErr ? ` (${loadErr})` : ""}</div>;

  const inviteLockedHere = occ?.mode === "invite_only" && !(invite?.valid && invite?.occurrence?.id === occ.id);
  const approvalPending = occ?.mode === "approval" && approval?.status === "pending";
  const approvalApproved = occ?.mode === "approval" && approval?.status === "approved";
  const unavailableStatus = event.customer_status === "cancelled" || occ?.availability === "cancelled"
    ? "cancelled"
    : event.customer_status === "ended" || occ?.availability === "ended"
      ? "ended"
      : null;
  const canOpenBooking = occ && !unavailableStatus && !inviteLockedHere
    && !(occ.mode === "approval" && !approvalApproved);
  const liked = social?.isLiked(id);
  const wished = social?.isWishlisted(id);
  const shareUrl = `${window.location.origin}/events/${id}`;
  const rating = event.rating || {};
  const closedKey = occ?.mode === "rsvp" ? "rsvp" : occ?.mode === "invite_only" ? "invite" : occ?.mode === "approval" ? "approval" : "tickets";
  const occClosed = !!event.closed?.[closedKey];

  return (
    <div>
      <RefreshBar refreshing={refreshing} lastUpdated={lastUpdated} onRefresh={refresh} />
      <div className="relative">
        <div className="h-[38vh] md:h-[46vh] max-h-[80vh] relative overflow-hidden">
          <img src={mediaUrl(event.banner)} alt={event.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-black/20" />
        </div>
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <button onClick={() => navigate(-1)} className="absolute top-4 left-4 md:left-8 z-10 inline-flex items-center gap-1 px-3 h-9 rounded-full glass text-sm font-medium"><ArrowLeft size={16} /> Back</button>
          <div className="-mt-24 md:-mt-28 relative z-10 grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              <div className="rounded-xl overflow-hidden w-40 md:w-48 shadow-xl border border-border">
                <SmartImage src={mediaUrl(event.banner)} alt={event.title} ratio="poster" rounded="rounded-none" />
              </div>
              <div className="flex items-center flex-wrap gap-2 mt-4">
                <span className="inline-block px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold capitalize">{event.genre}</span>
                {rating.count > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold"><Star size={13} weight="fill" className="text-amber-500" /> {rating.average} <span className="text-muted-foreground font-normal">({rating.count})</span></span>
                )}
                <div className="flex-1" />
                <button onClick={onLike} data-testid="like-event-btn"
                  className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full border text-xs font-semibold transition-colors ${liked ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  <Heart size={15} weight={liked ? "fill" : "regular"} /> {liked ? "Liked" : "Like"}
                </button>
                <button onClick={onWishlist} data-testid="wishlist-event-btn"
                  className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full border text-xs font-semibold transition-colors ${wished ? "border-navy text-navy bg-navy/5" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  <BookmarkSimple size={15} weight={wished ? "fill" : "regular"} /> {wished ? "Saved" : "Notify me"}
                </button>
                <ShareMenu url={shareUrl} title={event.title} text={`Check out ${event.title} on EventVexa`} testid="event-share"
                  trigger={
                    <button data-testid="share-event-btn" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full border border-border text-xs font-semibold text-muted-foreground hover:text-foreground"><ShareNetwork size={15} /> Share</button>
                  } />
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold tracking-tighter mt-3 leading-tight">{event.title}</h1>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><CalendarBlank size={16} className="text-primary" /> {(event.dates || []).map(fmtDate).join(" · ") || "TBA"}</span>
                <span className="inline-flex items-center gap-1.5"><MapPin size={16} className="text-primary" /> {(event.cities || []).join(", ")}</span>
              </div>
              {event.organizer_name && <p className="mt-2 text-sm text-muted-foreground">By <span className="font-semibold text-foreground">{event.organizer_name}</span></p>}

              <div className="mt-8 space-y-8">
                <div>
                  <h3 className="font-heading text-xl font-semibold mb-2">About</h3>
                  <p className="text-foreground/80 leading-relaxed">{event.description}</p>
                </div>
                {(event.gallery || []).length > 0 && (
                  <div data-testid="event-gallery">
                    <h3 className="font-heading text-xl font-semibold mb-3">Gallery</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {event.gallery.map((m, i) => (
                        <div key={m.id || i} className="rounded-xl overflow-hidden border border-border aspect-video bg-muted">
                          {m.kind === "video"
                            ? <video src={mediaUrl(m.url)} controls className="w-full h-full object-cover" />
                            : <img src={mediaUrl(m.url)} alt="" loading="lazy" className="w-full h-full object-cover" />}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {occ && (occ.venue || occ.city) && (
                  <div data-testid="event-map">
                    <h3 className="font-heading text-xl font-semibold mb-2">Getting there</h3>
                    <p className="text-sm text-muted-foreground mb-3">{occ.venue}{occ.venue && occ.city ? ", " : ""}{occ.city}</p>
                    <div className="rounded-xl overflow-hidden border border-border">
                      <iframe title="Venue map" width="100%" height="240" loading="lazy" style={{ border: 0 }}
                        src={`https://www.google.com/maps?q=${encodeURIComponent(`${occ.venue || ""}, ${occ.city || ""}`)}&output=embed`} />
                    </div>
                    <a href={occ.map_link || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${occ.venue || ""}, ${occ.city || ""}`)}`}
                      target="_blank" rel="noreferrer" data-testid="open-maps-btn"
                      className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-primary hover:underline">
                      <MapPin size={15} /> Open in Google Maps
                    </a>
                  </div>
                )}
                {event.checkpoints?.length > 1 && (
                  <div>
                    <h3 className="font-heading text-xl font-semibold mb-2 flex items-center gap-2"><ShieldCheck size={20} className="text-primary" /> Access checkpoints</h3>
                    <div className="flex flex-wrap gap-2">
                      {event.checkpoints.map((c) => <span key={c.id} className="px-3 py-1.5 rounded-full bg-muted text-sm">{c.name}</span>)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">Different ticket categories grant access to different checkpoints.</p>
                  </div>
                )}
                {event.highlights?.length > 0 && (
                  <div>
                    <h3 className="font-heading text-xl font-semibold mb-3">Highlights</h3>
                    <div className="flex flex-wrap gap-2">{event.highlights.map((h, i) => <span key={i} className="px-3 py-1.5 rounded-full bg-muted text-sm">{h}</span>)}</div>
                  </div>
                )}
                {event.faqs?.length > 0 && (
                  <div>
                    <h3 className="font-heading text-xl font-semibold mb-2">FAQs</h3>
                    <Accordion type="single" collapsible className="w-full">
                      {event.faqs.map((f, i) => (
                        <AccordionItem key={i} value={`f${i}`}>
                          <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                          <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                )}
                {(event.terms || event.refund_policy || event.age_restriction) && (
                  <div className="text-sm text-muted-foreground space-y-2 border-t border-border pt-6">
                    {event.age_restriction && <p><span className="font-semibold text-foreground">Age:</span> {event.age_restriction}</p>}
                    {event.terms && <p><span className="font-semibold text-foreground">Terms:</span> {event.terms}</p>}
                    {event.refund_policy && <p><span className="font-semibold text-foreground">Refunds:</span> {event.refund_policy}</p>}
                  </div>
                )}
                <div className="border-t border-border pt-6"><Reviews eventId={id} /></div>
              </div>
            </div>

            {/* sticky booking card */}
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-20 rounded-xl border border-border bg-card p-6">
                {(event.occurrences || []).length > 1 && (
                  <div className="mb-4">
                    <div className="overline mb-2">Choose date & city</div>
                    <div className="space-y-2 max-h-56 overflow-y-auto">
                      {event.occurrences.map((o) => (
                        <button key={o.id} onClick={() => setSelOcc(o.id)} data-testid={`occurrence-${o.id}`}
                          className={`w-full text-left p-3 rounded-lg border transition-colors ${selOcc === o.id ? "border-primary bg-primary/5" : "border-border hover:border-foreground/30"}`}>
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-sm">{fmtDate(o.date)}</span>
                            {o.availability === "cancelled" ? <StatusBadge status="cancelled" />
                              : o.availability === "ended" ? <StatusBadge status="completed" />
                                : MODE_LABEL[o.mode] && <span className="text-[10px] font-bold uppercase text-navy bg-navy/10 px-2 py-0.5 rounded-full">{MODE_LABEL[o.mode]}</span>}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">{o.venue}, {o.city}{o.label ? ` · ${o.label}` : ""}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {unavailableStatus ? (
                  <div className="rounded-lg border border-border bg-muted p-4 text-sm" data-testid="event-unavailable-status">
                    <div className="font-semibold text-foreground">
                      {unavailableStatus === "cancelled" ? "Event Cancelled" : "Event ended"}
                    </div>
                    <p className="mt-1 text-muted-foreground">
                      {unavailableStatus === "cancelled"
                        ? "This event is no longer accepting bookings."
                        : "This event has finished and is no longer accepting bookings."}
                    </p>
                  </div>
                ) : occ && (
                  <>
                    <div className="text-sm text-muted-foreground">{occ.is_free ? "Free entry" : "Starting from"}</div>
                    <div className="font-heading text-3xl font-bold tracking-tight">
                      {occ.is_free ? "Free" : money(Math.min(...(occ.ticket_categories || []).map((c) => c.price)))}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1.5"><Clock size={13} /> {occ.time || "TBA"} · {occ.venue}, {occ.city}</div>

                    <div className="mt-4 space-y-2">
                      {(occ.ticket_categories || []).map((c) => {
                        const left = c.quantity - c.sold;
                        const low = left > 0 && c.low_stock_threshold && left <= c.low_stock_threshold;
                        return (
                          <div key={c.id} className="flex items-center justify-between text-sm py-1.5 border-b border-border/60 last:border-0">
                            <div>
                              <div className="font-medium">{c.name}</div>
                              <div className={`text-xs ${left <= 0 ? "text-destructive" : low ? "text-amber-600 font-semibold" : "text-muted-foreground"}`}>
                                {left > 0 ? (low ? `Only ${left} left!` : `${left} available`) : (c.waitlist_enabled ? "Sold out · waitlist open" : "Sold out")}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold">{occ.is_free || !paid ? "Free" : money(c.price)}</div>
                              {left <= 0 && c.waitlist_enabled && (
                                <button onClick={() => joinWaitlist(c.id)} data-testid={`join-waitlist-${c.id}`} className="text-xs text-primary font-semibold">Join waitlist</button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {occClosed ? (
                      <div className="mt-5 rounded-lg bg-muted p-4 text-sm flex items-start gap-2" data-testid="occ-closed">
                        <LockKey size={18} className="text-muted-foreground mt-0.5" />
                        <div>{closedKey === "tickets" ? "Ticket sales have closed for this event." : "The response period for this event has ended."} Existing confirmations remain valid.</div>
                      </div>
                    ) : inviteLockedHere ? (
                      <div className="mt-5 rounded-lg bg-muted p-4 text-sm flex items-start gap-2">
                        <LockKey size={18} className="text-navy mt-0.5" />
                        <div>This is an invite-only show. Please use the private invite link shared with you.</div>
                      </div>
                    ) : approvalPending ? (
                      <div className="mt-5 rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm flex items-start gap-2" data-testid="approval-pending">
                        <HourglassMedium size={18} className="text-amber-600 mt-0.5" />
                        <div>Your request to attend is pending organizer approval. We'll notify you once it's approved.</div>
                      </div>
                    ) : occ.mode === "approval" && !approvalApproved ? (
                      <Button onClick={requestApproval} data-testid="request-approval-btn" className="w-full h-12 mt-5 rounded-full text-base">
                        <CheckCircle weight="fill" className="mr-2" /> Request to attend
                      </Button>
                    ) : (
                      <Button onClick={openBooking} disabled={!canOpenBooking} data-testid="book-now-btn" className="w-full h-12 mt-5 rounded-full text-base">
                        {approvalApproved ? <><CheckCircle weight="fill" className="mr-2" /> Approved — Get ticket</>
                          : occ.mode === "rsvp" || !paid ? <><Confetti weight="fill" className="mr-2" /> RSVP / Confirm</>
                            : <><Ticket weight="fill" className="mr-2" /> Book Tickets</>}
                      </Button>
                    )}
                    <p className="mt-3 text-xs text-muted-foreground inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-emerald-600" /> Secure checkout · Instant QR ticket</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* booking dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{paid ? "Book" : "Confirm"} · {event.title}</DialogTitle></DialogHeader>
          {occ && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground -mt-1">{fmtDate(occ.date)} · {occ.venue}, {occ.city}</div>
              {(occ.ticket_categories || []).map((c) => {
                const left = c.quantity - c.sold;
                const cap = Math.min(left, c.per_txn_limit || 10);
                return (
                  <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border border-border">
                    <div>
                      <div className="font-medium">{c.name} <span className="text-xs text-muted-foreground">({c.type})</span></div>
                      <div className="text-sm text-muted-foreground">{occ.is_free || !paid ? "Free" : money(c.price)} · {left > 0 ? `${left} left` : "Sold out"} · max {c.per_txn_limit || 10}/txn</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => step(c.id, -1, cap)} disabled={!qty[c.id]} data-testid={`dec-${c.id}`} className="w-8 h-8 grid place-items-center rounded-full border border-border disabled:opacity-40 btn-press"><Minus size={14} /></button>
                      <span className="w-6 text-center font-semibold" data-testid={`qty-${c.id}`}>{qty[c.id] || 0}</span>
                      <button onClick={() => step(c.id, 1, cap)} disabled={cap <= (qty[c.id] || 0)} data-testid={`inc-${c.id}`} className="w-8 h-8 grid place-items-center rounded-full bg-foreground text-background disabled:opacity-40 btn-press"><Plus size={14} /></button>
                    </div>
                  </div>
                );
              })}

              {paid && (
                <>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Tag size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input data-testid="coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code" className="pl-9 h-10" />
                    </div>
                    <Button variant="outline" onClick={() => setQuoteNonce((n) => n + 1)} data-testid="apply-coupon">Apply</Button>
                  </div>
                  <label className="flex items-center justify-between p-3 rounded-lg border border-border cursor-pointer">
                    <span className="inline-flex items-center gap-2 text-sm"><Gift size={18} className="text-primary" /> Use my Event Credits</span>
                    <Switch checked={useCredits} onCheckedChange={setUseCredits} data-testid="use-credits" />
                  </label>
                </>
              )}

              {paid && quote && (
                <div className="rounded-lg bg-muted p-4 text-sm space-y-1.5" data-testid="price-breakup">
                  <Row l="Subtotal" v={money(quote.subtotal)} />
                  {quote.discount > 0 && <Row l="Coupon discount" v={`− ${money(quote.discount)}`} accent />}
                  {quote.convenience_fee > 0 && <Row l="Convenience fee" v={money(quote.convenience_fee)} />}
                  {quote.gst > 0 && <Row l="GST" v={money(quote.gst)} />}
                  {quote.credits_amount > 0 && <Row l={`Event credits (${quote.credits_points} pts)`} v={`− ${money(quote.credits_amount)}`} accent />}
                  <div className="h-px bg-border my-2" />
                  <div className="flex justify-between font-heading text-lg font-semibold"><span>Total</span><span data-testid="total-amount">{money(quote.total)}</span></div>
                  {quote.earn_points > 0 && <div className="text-xs text-emerald-700 pt-1">You'll earn {quote.earn_points} credits on this booking.</div>}
                </div>
              )}
              {!paid && items.length > 0 && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800">Complimentary entry — no payment required. Confirm to get your QR ticket.</div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={pay} disabled={!items.length || paying} data-testid="pay-btn" className="w-full h-12 rounded-full text-base">
              {paying ? "Processing…" : !items.length ? "Select tickets" : paid ? `Pay ${quote ? money(quote.total) : ""}` : "Confirm attendance"}
            </Button>
          </DialogFooter>
          {paid && <p className="text-xs text-center text-muted-foreground -mt-2">{rzpCfg?.enabled ? "Secured by Razorpay · UPI, cards, netbanking & wallets." : "Simulated payment for demo. Razorpay-ready."}</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ l, v, accent }) {
  return <div className="flex justify-between"><span className="text-muted-foreground">{l}</span><span className={accent ? "text-emerald-700 font-medium" : ""}>{v}</span></div>;
}
